/* eslint-disable @typescript-eslint/no-explicit-any */
import Icon from "@material-ui/core/Icon";
import { makeStyles } from "@material-ui/core/styles";
import { Launch } from "@material-ui/icons";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import Card from "components/UI/Card/Card.js";
import CardBody from "components/UI/Card/CardBody.js";
import CardFooter from "components/UI/Card/CardFooter";
import CardHeader from "components/UI/Card/CardHeader.js";
import CardIcon from "components/UI/Card/CardIcon.js";
import ButtonTS from "components/Forms/CustomButtons/ButtonTS";
import GridContainer from "components/UI/Grid/GridContainer";
import GridItem from "components/UI/Grid/GridItem";
import Table from "components/Tables/Table/Table.js";

import * as dateFns from "date-fns";
import {enUS, ptBR} from "date-fns/locale";
import es from "date-fns/locale/es";
import it from "date-fns/locale/it";
import { ProfileTab } from "enums/ProfileTabs";
import { getDocumentTypeByCode } from "helpers/activityNameHelper";
import { WorkItemProfiles } from "helpers/profileEnum";
import React from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { useNavigate } from "react-router-dom";
import { DashboardWorkItem } from "services/input-core/dashboard/dashboard";
import { useSubscription } from "store/auth/auth-store";
import { ProfileEnum } from "utils/@types";

const styleAny: any = styles;
const useStyles = makeStyles(styleAny);

const locales: Record<string, dateFns.Locale> = {
  en: enUS,
  es,
  it,
  pt: ptBR
};

interface StatCardProps {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  tableSize?: {
    justify?:
      | "space-between"
      | "flex-end"
      | "center"
      | "flex-start"
      | "space-around"
      | "space-evenly"
      | undefined;
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
  };
  color: "warning" | "success" | "danger" | "info" | "primary";
  icon: string;
  title: string;
  isLoading: boolean;
  value: DashboardWorkItem[];
  onClick?: () => void;
}

const ProcessListCard: React.FC<StatCardProps> = ({
  color,
  icon,
  title,
  isLoading,
  value,
  onClick,
  tableSize: { justify, ...sizes },
  ...rest
}) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation([
    "dashboard",
    "common",
    "analysis-modal",
    "activity-item-table",
  ]);
  const { isKuo } = useSubscription();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tableHeaders = [
    "supplier",
    "activity",
    "description",
    "ID",
    "due-date",
    "processed-at",
  ];

  const tableHeadersFormatted = tableHeaders.map((header) => t(`${header}`));

  const formatProcessesIntoArrays = (processes: DashboardWorkItem[]) => {
    function redirectToProfile(process: DashboardWorkItem) {
      const auxDate = process?.activityItemDueDate.split("/");

      const date = new Date(
        title != "last-pending"
          ? process.activityItemDueDate
          : `${auxDate[1]}-${auxDate[0]}-${auxDate[2]}`,
      );
      const month = date.getMonth() + 1;
      const year = date.getFullYear();

      if (isKuo && process?.profile !== ProfileEnum.WORKER) {
        return navigate(
          `../${WorkItemProfiles["SOWADM"]}/profile?id=${process.requestParam}&tab=${ProfileTab.ACTIVITY_ITEMS}&month=${month}&year=${year}&activity=${process.type}`,
        );
      }

      navigate(
        `../${WorkItemProfiles[process.profile as never]}/profile?id=${
          process.requestParam
        }&tab=${ProfileTab.ACTIVITY_ITEMS}${
          title != "last-pending" ? "&activity=" + process.type : ""
        }&month=${month}&year=${year}`,
      );
    }

    return processes.map((process) => {
      return [
        process?.supplier?.supplierName,
        getDocumentTypeByCode(process?.type),
        process?.activityItem?.activityItemDescription,
        process?.requestParam,
        new Date(process?.activityItemDueDate).toLocaleString("pt-BR", {
          timeZone: "UTC",
          year: "numeric",
          month: "numeric",
          day: "numeric",
        }),
        i18n.language === "es"
          ? "hace " +
            dateFns.formatDistanceToNowStrict(new Date(process?.updatedAt), {
              locale: locales[i18n.language],
            })
          : dateFns.formatDistanceToNowStrict(new Date(process?.updatedAt), {
              locale: locales[i18n.language],
            }) +
            " " +
            t("common:ago"),
        <ButtonTS
          round
          color="info"
          className={classes.actionButton + " " + classes.actionButtonRound}
          onClick={() => redirectToProfile(process)}
          key={new Date().getTime()}
        >
          <ArrowForwardIosIcon className={classes.icon} />
        </ButtonTS>,
      ];
    });
  };

  const refetch = () => {
    queryClient.invalidateQueries(["dashboard", "stats", "workitem"]);
  };

  return (
    <GridItem {...rest}>
      <Card>
        <CardHeader color={color} icon>
          <CardIcon color={color}>
            <Icon>{icon}</Icon>
          </CardIcon>
          <h4 className={classes.cardIconTitle}>{t(title)}</h4>
        </CardHeader>
        <CardBody>
          <GridContainer {...{ justify }}>
            <GridItem {...sizes}>
              <Table
                id={`${title}-table`}
                tableHead={tableHeadersFormatted}
                tableData={
                  isLoading
                    ? [[<Skeleton />, <Skeleton />, <Skeleton />]]
                    : value
                    ? formatProcessesIntoArrays(value)
                    : [
                        [
                          t("no-process"),
                          "",
                          <ButtonTS key={0} onClick={refetch}>
                            Retry
                          </ButtonTS>,
                        ],
                      ]
                }
                key={new Date().getTime()}
              />
            </GridItem>
            <div onClick={onClick} style={{ cursor: "pointer" }}>
              <CardFooter stats>
                <Typography color="primary">
                  <Launch />
                  {t("view-all")}
                </Typography>
              </CardFooter>
            </div>
          </GridContainer>
        </CardBody>
      </Card>
    </GridItem>
  );
};

export default ProcessListCard;
